// Dashboards admin client — per-(tenant, alias) saved camera walls.
//
// Endpoints (REB service, P-D wave-1):
//   GET    /api/dashboards                    list caller's dashboards
//   POST   /api/dashboards                    create (caller becomes owner)
//   GET    /api/dashboards/{dashboard_id}     read one (404 if not owned)
//   PATCH  /api/dashboards/{dashboard_id}     update (display_name, tiles)
//   DELETE /api/dashboards/{dashboard_id}     delete
//
// Authorization: any authenticated session can read/write its own
// dashboards. Cross-alias reads return 404 to avoid leaking existence.

import { api } from "./client";
import type {
  CreateDashboardRequest,
  Dashboard,
  UpdateDashboardRequest,
} from "@/types/dashboard";

interface ListResponse {
  items?: Dashboard[];
  dashboards?: Dashboard[];
}

// Single-dashboard responses (GET /{id}, POST, PATCH) wrap the resource in a
// `dashboard` envelope: {"dashboard": {...}}. Older/local shapes may return the
// bare object, so unwrap defensively.
interface SingleResponse {
  dashboard?: Dashboard;
}

const BASE = "/api/dashboards";

function unwrap(resp: ListResponse): Dashboard[] {
  if (Array.isArray(resp.items)) return resp.items;
  if (Array.isArray(resp.dashboards)) return resp.dashboards;
  return [];
}

function unwrapOne(resp: Dashboard & SingleResponse): Dashboard {
  // Prefer the enveloped resource; fall back to a bare object (has `tiles`).
  if (resp.dashboard && typeof resp.dashboard === "object") {
    return resp.dashboard;
  }
  return resp;
}

export const dashboardsClient = {
  async list(): Promise<Dashboard[]> {
    const resp = await api.get<ListResponse>(BASE);
    return unwrap(resp);
  },

  async get(dashboardId: string): Promise<Dashboard> {
    const resp = await api.get<Dashboard & SingleResponse>(
      `${BASE}/${encodeURIComponent(dashboardId)}`,
    );
    return unwrapOne(resp);
  },

  async create(req: CreateDashboardRequest): Promise<Dashboard> {
    const resp = await api.post<Dashboard & SingleResponse>(BASE, req);
    return unwrapOne(resp);
  },

  async update(
    dashboardId: string,
    req: UpdateDashboardRequest,
    version: number,
  ): Promise<Dashboard> {
    const resp = await api.patch<Dashboard & SingleResponse>(
      `${BASE}/${encodeURIComponent(dashboardId)}`,
      req,
      { headers: { "If-Match": String(version) } },
    );
    return unwrapOne(resp);
  },

  async remove(dashboardId: string, version: number): Promise<void> {
    await api.delete(`${BASE}/${encodeURIComponent(dashboardId)}`, {
      headers: { "If-Match": String(version) },
    });
  },
};
